// Settings: own account, devices and sessions, the Android app, automatic deletion of done reminders,
// vocabulary for better recognition, silence trimming before transcription (savings), upload queue,
// administration (users, security, audit log), server features, storage, logout.
import { useEffect, useRef, useState } from 'react';
import { getSettings, isFromCache, saveSettings } from '../lib/api';
import { isAndroidApp } from '../lib/android';
import { buildVersion, isOutdated, refreshApp } from '../lib/version';
import { formatDateTime, plural } from '../lib/format';
import { useFeatures, useOutboxStatus } from '../lib/hooks';
import { clearFailed, flush, type FailedOp, type OutboxOpKind, type OutboxStatus } from '../lib/outbox';
import { copyText } from '../export';
import { confirmDialog } from '../components/Dialog';
import { errorText, formatBytes, onPageHide, useLatest } from '../components/helpers';
import { Icon } from '../components/Icon';
import { confirmAndLogout } from '../components/Logout';
import { Link } from '../components/NavGuard';
import { toast } from '../components/Toasts';
import { AccountSection } from './settings/AccountSection';
import { AndroidSection } from './settings/AndroidSection';
import { AuditSection } from './settings/AuditSection';
import { ProjectsSection } from './settings/ProjectsSection';
import { RemindersSection } from './settings/RemindersSection';
import { SavingsSection } from './settings/SavingsSection';
import { SecuritySection } from './settings/SecuritySection';
import { SessionsSection } from './settings/SessionsSection';
import { UsersSection } from './settings/UsersSection';

const OP_LABEL: Record<OutboxOpKind, string> = {
  createDocument: 'Nov dokument',
  updateDocument: 'Sprememba dokumenta',
  deleteDocument: 'Brisanje dokumenta',
  createAudioClip: 'Prenos posnetka',
  createTextClip: 'Nov vnos',
  updateClip: 'Popravek besedila',
  deleteClip: 'Brisanje vnosa',
  retranscribeClip: 'Ponovni prepis',
  updateSegment: 'Popravek prepisa',
  saveSettings: 'Shranjevanje nastavitev',
};

const PROVIDER_LABEL: Record<string, string> = {
  soniox: 'Soniox',
  elevenlabs: 'ElevenLabs',
  openai: 'OpenAI',
  mock: 'Preizkusni način (brez pravega prepisa)',
};

function parseVocabulary(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

const termKey = (term: string) => term.trim().toLocaleLowerCase('sl');
/** Saving waits at most this long for the current server list before using the loaded one. */
const FRESH_READ_TIMEOUT_MS = 5_000;

/** Terms in `latest` that were added after `base` was loaded and are not in `mine`. */
function addedByOthers(base: readonly string[], mine: readonly string[], latest: readonly string[]): number {
  const known = new Set([...base, ...mine].map(termKey));
  return new Set(latest.map(termKey).filter((k) => !known.has(k))).size;
}

/** The current server list, or null when it cannot be read quickly. */
async function readLatestVocabulary(): Promise<string[] | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), FRESH_READ_TIMEOUT_MS);
  });
  try {
    const fresh = await Promise.race([getSettings(), timeout]);
    return fresh && !isFromCache(fresh) ? fresh.vocabulary : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The vocabulary is shared by the whole firm and the server replaces it as a whole. Saving applies
 * this device's edit (`mine` compared with the `base` it started from) to the `latest` server list,
 * so terms colleagues added or removed meanwhile are kept as they are.
 */
export function mergeVocabulary(base: readonly string[], mine: readonly string[], latest: readonly string[]): string[] {
  const baseKeys = new Set(base.map(termKey));
  const latestKeys = new Set(latest.map(termKey));
  const mineKeys = new Set(mine.map(termKey));
  // Mine, without terms someone else has removed meanwhile (unless I added them myself).
  const out = mine.filter((t) => !baseKeys.has(termKey(t)) || latestKeys.has(termKey(t)));
  // Plus what others added meanwhile.
  for (const t of latest) if (!baseKeys.has(termKey(t)) && !mineKeys.has(termKey(t))) out.push(t);
  return out;
}

function VocabularyCard() {
  const [text, setText] = useState('');
  /** The list this edit started from (null until loaded). */
  const [base, setBase] = useState<string[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Only the copy stored on this device is known: editing it would replace newer terms on the server.
  const [fromCache, setFromCache] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    setLoadError(null);
    getSettings().then(
      (s) => {
        setText(s.vocabulary.join('\n'));
        setBase(s.vocabulary);
        setFromCache(isFromCache(s));
        setLoading(false);
      },
      (err: unknown) => {
        setLoadError(errorText(err, 'Nastavitev ni bilo mogoče naložiti.'));
        setLoading(false);
      },
    );
  };
  useEffect(load, []);

  const dirty = base !== null && !fromCache && text !== base.join('\n');

  /**
   * Saves the edit. `leaving`: the page is being left or unloaded – no state updates, and on unload
   * no server read first (the page may be gone before it answers).
   */
  const save = async (leaving: false | 'unmount' | 'unload' = false) => {
    if (base === null || fromCache) return;
    const quiet = leaving !== false;
    const mine = parseVocabulary(text);
    if (!quiet) setSaving(true);
    try {
      // Unreachable or slow: the edit is applied to the list it started from.
      const latest = (leaving === 'unload' ? null : await readLatestVocabulary()) ?? base;
      const others = addedByOthers(base, mine, latest);
      const result = await saveSettings({ vocabulary: mergeVocabulary(base, mine, latest) });
      if (!quiet) {
        setText(result.vocabulary.join('\n'));
        setBase(result.vocabulary);
      }
      toast(
        others > 0
          ? `Slovar je shranjen. Dodani so tudi izrazi, ki so jih medtem vnesli drugi (${others}).`
          : 'Slovar je shranjen. Upošteva se pri naslednjih prepisih.',
        { kind: 'success' },
      );
    } catch (err) {
      toast(errorText(err, 'Slovarja ni bilo mogoče shraniti.'), { kind: 'error' });
    } finally {
      if (!quiet) setSaving(false);
    }
  };

  // Typed terms are not lost when the page is left, hidden or reloaded without saving.
  const pending = useLatest({ dirty, saving, save });
  const flushed = useRef(false);
  useEffect(() => {
    const flush = (how: 'unmount' | 'unload') => {
      const p = pending.current;
      if (!p.dirty || p.saving || flushed.current) return;
      flushed.current = true;
      void p.save(how);
    };
    const stop = onPageHide(() => flush('unload'));
    return () => {
      stop();
      flush('unmount');
    };
  }, [pending]);
  useEffect(() => {
    flushed.current = false;
  }, [text]);

  const count = parseVocabulary(text).length;

  return (
    <section className="card" aria-labelledby="vocab-title">
      <h2 id="vocab-title" className="card__title">
        Slovar izrazov
      </h2>
      <p className="muted">
        En izraz v vsako vrstico: imena oseb, krajev in strokovni izrazi – izboljšajo prepoznavo (npr. »Kekec d.o.o.«, »armiranobetonska
        plošča«, »PZI«). Imena projektov iz kartice Projekti se upoštevajo samodejno.
      </p>
      {fromCache && !loadError && (
        <div className="notice notice--warning" role="status">
          <Icon name="cloudOff" size={20} className="notice__icon" />
          <div className="notice__content">
            <p>Strežnik ni dosegljiv – slovarja zdaj ni mogoče urejati. Prikazan je seznam, shranjen v tej napravi.</p>
            <button type="button" className="btn btn--secondary btn--small" onClick={load} disabled={loading}>
              <Icon name={loading ? 'spinner' : 'refresh'} size={18} /> Poskusi znova
            </button>
          </div>
        </div>
      )}
      {loadError ? (
        <div className="notice notice--error" role="alert">
          <Icon name="alert" size={20} className="notice__icon" />
          <div className="notice__content">
            <p>{loadError}</p>
            <button type="button" className="btn btn--secondary btn--small" onClick={load}>
              <Icon name="refresh" size={18} /> Poskusi znova
            </button>
          </div>
        </div>
      ) : (
        <>
          <label htmlFor="vocabulary" className="sr-only">
            Slovar izrazov
          </label>
          <textarea
            id="vocabulary"
            className="vocabulary"
            rows={8}
            value={text}
            disabled={loading || fromCache || saving}
            placeholder={loading ? 'Nalagam …' : 'npr.\nJanez Novak\nVrtec Kekec\nfasadni ovoj'}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
          />
          <div className="card__foot">
            <span className="muted small">{plural(count, ['izraz', 'izraza', 'izrazi', 'izrazov'])}</span>
            <button type="button" className="btn btn--primary" onClick={() => void save()} disabled={loading || saving || fromCache || !dirty}>
              <Icon name={saving ? 'spinner' : 'check'} size={20} /> Shrani slovar
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function FeaturesCard() {
  const { data: features, error, stale, reload } = useFeatures();
  return (
    <section className="card" aria-labelledby="features-title">
      <h2 id="features-title" className="card__title">
        Strežnik
      </h2>
      {!features ? (
        error ? (
          <p className="muted">
            {error}{' '}
            <button type="button" className="btn btn--link" onClick={reload}>
              Poskusi znova
            </button>
          </p>
        ) : (
          <p className="muted loading-line">
            <Icon name="spinner" size={18} /> Nalagam …
          </p>
        )
      ) : (
        <>
          <dl className="facts">
            <dt>Ponudnik prepisa</dt>
            <dd>{PROVIDER_LABEL[features.provider] ?? (features.provider || 'neznan')}</dd>
            <dt>Prepoznava govorcev</dt>
            <dd>{features.diarization ? 'da' : 'ne'}</dd>
            <dt>UI funkcije (zapisnik, urejanje)</dt>
            <dd>{features.ai ? 'da' : 'ne'}</dd>
            <dt>Največja velikost posnetka</dt>
            <dd>{formatBytes(features.maxUploadBytes)}</dd>
            <dt>Različica programa</dt>
            <dd data-testid="server-version">{features.version ?? 'neznana'}</dd>
            <dt>Spletna aplikacija</dt>
            <dd data-testid="web-version">
              {buildVersion()}
              {isOutdated(buildVersion(), features.version) && (
                <>
                  {' '}
                  –{' '}
                  <button type="button" className="btn btn--link" onClick={() => void refreshApp()}>
                    osvežite stran za novo različico
                  </button>
                </>
              )}
            </dd>
          </dl>
          {stale && <p className="muted small">Strežnik trenutno ni dosegljiv – prikazani so zadnji znani podatki.</p>}
        </>
      )}
    </section>
  );
}

/** "3 spremembe, od tega 1 posnetek (2 MB)"; with storage not answering the count may be incomplete. */
export function pendingText(status: OutboxStatus): string {
  if (status.pending === 0) return status.pendingUnknown ? 'neznano – shramba brskalnika se ni odzvala' : 'nič – vse je shranjeno na strežniku';
  const uploads =
    status.pendingUploads > 0
      ? `, od tega ${plural(status.pendingUploads, ['posnetek', 'posnetka', 'posnetki', 'posnetkov'])} (${formatBytes(status.pendingBytes)})`
      : '';
  const unknown = status.pendingUnknown ? ' – morda še več, shramba brskalnika se ni odzvala' : '';
  return `${plural(status.pending, ['sprememba', 'spremembi', 'spremembe', 'sprememb'])}${uploads}${unknown}`;
}

async function copyDetail(f: FailedOp): Promise<void> {
  const ok = await copyText(f.detail ?? '');
  toast(ok ? 'Besedilo je kopirano v odložišče.' : 'Brskalnik ni dovolil kopiranja v odložišče.', { kind: ok ? 'success' : 'warning' });
}

async function clearFailedList(): Promise<void> {
  const ok = await confirmDialog({
    title: 'Počisti seznam?',
    message: 'Seznam zavrnjenih sprememb in njihovo besedilo bosta izbrisana iz te naprave. Zavrnjeni posnetki ostanejo na voljo za obnovitev.',
    confirmLabel: 'Počisti',
    danger: true,
  });
  if (!ok) return;
  try {
    await clearFailed();
  } catch (err) {
    toast(errorText(err, 'Seznama ni bilo mogoče počistiti.'), { kind: 'error' });
  }
}

function OutboxCard() {
  const status = useOutboxStatus();
  return (
    <section className="card" aria-labelledby="outbox-title">
      <h2 id="outbox-title" className="card__title">
        Pošiljanje na strežnik
      </h2>
      <dl className="facts">
        <dt>Povezava</dt>
        <dd>{status.online ? 'na voljo' : 'brez povezave'}</dd>
        <dt>Čaka na prenos</dt>
        <dd>{pendingText(status)}</dd>
        {status.syncing && (
          <>
            <dt>Stanje</dt>
            <dd>pošiljam …</dd>
          </>
        )}
      </dl>
      {status.lastError && status.pending > 0 && (
        <p className="form-error" role="status">
          {status.lastError}
        </p>
      )}
      <div className="card__foot">
        <span className="muted small">Spremembe se pošiljajo samodejno, ko je povezava na voljo.</span>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => flush()}
          disabled={(status.pending === 0 && !status.pendingUnknown) || status.syncing}
        >
          <Icon name={status.syncing ? 'spinner' : 'refresh'} size={20} /> Poskusi zdaj
        </button>
      </div>
      {status.failed.length > 0 && (
        <>
          <h3 className="subheading">Zavrnjene spremembe</h3>
          <p className="muted small">
            Strežnik teh sprememb ni sprejel. Zavrnjeni posnetki ostanejo v napravi in so takoj ponujeni za obnovitev (če izberete
            »Pozneje«, ob naslednjem zagonu aplikacije).
          </p>
          <ul className="failed-list">
            {status.failed.map((f, i) => (
              <li key={`${f.at}-${i}`}>
                <span className="failed-list__kind">{OP_LABEL[f.kind]}</span>
                <span className="failed-list__msg">{f.message}</span>
                {/* The refused text itself, so it can still be copied. */}
                {f.detail && <span className="failed-list__detail">{f.detail}</span>}
                <span className="failed-list__actions">
                  <span className="muted small">{formatDateTime(f.at)}</span>
                  {f.detail && (
                    <button
                      type="button"
                      className="btn btn--link btn--small"
                      onClick={() => void copyDetail(f)}
                      aria-label={`Kopiraj besedilo: ${OP_LABEL[f.kind]}, ${formatDateTime(f.at)}`}
                    >
                      <Icon name="copy" size={18} /> Kopiraj
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <div className="failed-foot">
            <button type="button" className="btn btn--ghost btn--small" onClick={() => void clearFailedList()}>
              <Icon name="trash" size={18} /> Počisti seznam
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function StorageCard() {
  const supported = typeof navigator !== 'undefined' && navigator.storage !== undefined;
  const [estimate, setEstimate] = useState<{ usage: number; quota: number } | null>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    navigator.storage
      .estimate?.()
      .then((e) => {
        if (!cancelled) setEstimate({ usage: e.usage ?? 0, quota: e.quota ?? 0 });
      })
      .catch(() => {});
    navigator.storage
      .persisted?.()
      .then((p) => {
        if (!cancelled) setPersisted(p);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const persist = async () => {
    setBusy(true);
    try {
      const granted = await navigator.storage.persist();
      setPersisted(granted);
      toast(
        granted
          ? 'Trajno shranjevanje je vklopljeno – brskalnik podatkov ne bo samodejno brisal.'
          : isAndroidApp()
            ? 'Trajnega shranjevanja ni bilo mogoče vklopiti.'
            : 'Brskalnik trajnega shranjevanja ni odobril. Pogosto pomaga, če aplikacijo dodate na začetni zaslon.',
        { kind: granted ? 'success' : 'warning' },
      );
    } catch (err) {
      toast(errorText(err, 'Trajnega shranjevanja ni bilo mogoče vklopiti.'), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="storage-title">
      <h2 id="storage-title" className="card__title">
        Shramba v napravi
      </h2>
      {!supported ? (
        <p className="muted">Ta brskalnik ne poroča o porabi prostora.</p>
      ) : (
        <>
          <dl className="facts">
            <dt>Porabljeno</dt>
            <dd>{estimate ? `${formatBytes(estimate.usage)} od ${formatBytes(estimate.quota)}` : '—'}</dd>
            <dt>Trajno shranjevanje</dt>
            <dd>{persisted === null ? '—' : persisted ? 'vklopljeno' : 'ni vklopljeno'}</dd>
          </dl>
          <p className="muted small">
            Neposlani posnetki in spremembe so shranjeni v brskalniku. Trajno shranjevanje prepreči, da bi jih brskalnik ob pomanjkanju prostora
            izbrisal.
          </p>
          {persisted !== true && typeof navigator.storage.persist === 'function' && (
            <button type="button" className="btn btn--secondary" onClick={() => void persist()} disabled={busy}>
              <Icon name={busy ? 'spinner' : 'storage'} size={20} /> Trajno shranjevanje
            </button>
          )}
        </>
      )}
    </section>
  );
}

export function SettingsPage() {
  const features = useFeatures().data;
  const [loggingOut, setLoggingOut] = useState(false);
  const user = features?.authenticated ? features.user : null;
  const admin = user?.role === 'admin';
  const [inApp] = useState(isAndroidApp);

  const doLogout = async () => {
    setLoggingOut(true);
    try {
      await confirmAndLogout();
    } finally {
      setLoggingOut(false);
    }
  };

  return (
    <div className="page page--settings">
      <div className="doc-head">
        <Link to={{ name: 'home' }} className="back-link">
          <Icon name="chevronLeft" size={20} /> Začetek
        </Link>
        <h1 className="page-title">Nastavitve</h1>
        {user && (
          <p className="muted">
            Prijavljeni ste kot <strong>{user.displayName}</strong>.
          </p>
        )}
      </div>
      {/* Read again when 2FA was switched on or off elsewhere (whether it is required may have changed too). */}
      {user && <AccountSection key={`${user.id}:${user.totpEnabled}`} user={user} />}
      {user && <SessionsSection />}
      <AndroidSection />
      {user && <RemindersSection key={user.id} user={user} />}
      <ProjectsSection />
      <VocabularyCard />
      <SavingsSection />
      <OutboxCard />
      {user && admin && (
        <>
          <h2 className="section-label settings-group">Skrbništvo</h2>
          <UsersSection me={user} />
          <SecuritySection me={user} />
          <AuditSection />
        </>
      )}
      <FeaturesCard />
      <StorageCard />
      {features?.authRequired &&
        (inApp ? (
          <p className="muted small app-footer">Za odjavo aplikacije v njeni orodni vrstici izberite »Nastavitve« in nato »Odjava«.</p>
        ) : (
          <section className="card">
            <button type="button" className="btn btn--secondary btn--block" onClick={() => void doLogout()} disabled={loggingOut}>
              <Icon name={loggingOut ? 'spinner' : 'logout'} size={20} /> Odjava
            </button>
          </section>
        ))}
      <p className="muted small app-footer">BAAM Transkripcija · BAAM arhitekti</p>
    </div>
  );
}
